<template>
  <div class="stock-view-page">
    <StockDocumentPageHeading
      :title="$t('STOCK.SALES.PAGE_TITLE')"
      :code="isNew ? '' : document.code"
      :new-label="isNew ? $t('STOCK.SALES.NEW') : ''"
    />
    <v-card :loading="isProcessing" :disabled="isProcessing">
      <v-tabs v-model="tab" class="stock-color" left dark>
        <v-tab href="#main">{{ document.code || $t("STOCK.SALES.NEW") }}</v-tab>
        <v-spacer></v-spacer>
        <div class="buttons-tab d-flex align-center flex-wrap pa-2">
          <v-chip v-if="document.status" class="mr-2" :color="statusColor" small dark>
            {{ statusLabel }}
          </v-chip>
          <v-btn v-if="canCreateInvoice" class="mr-2 primary" small @click="openInvoiceDialog">
            {{ invoiceLinkButtonLabel }}
          </v-btn>
          <v-btn v-if="canFinalize" class="mr-2 success" small @click="finalizeSale">
            {{ $t("STOCK.SALES.FINALIZE") }}
          </v-btn>
          <v-btn v-if="canDiscard" class="mr-2 error" small @click="discardSale">
            {{ $t("STOCK.DOCUMENTS.DISCARD") }}
          </v-btn>
          <v-btn v-if="canSave" class="mr-2 success" small @click="save">
            {{ $t("GENERAL.LABELS.SAVE") }}
          </v-btn>
        </div>
      </v-tabs>

      <v-tabs-items v-model="tab">
        <v-tab-item value="main">
          <v-card flat>
            <v-card-text>
              <v-alert
                v-if="isReadOnlyDocument"
                type="warning"
                dense
                outlined
                class="mb-4"
              >
                {{ $t("STOCK.SALES.READ_ONLY_DOCUMENT") }}
              </v-alert>
              <v-row>
                <v-col cols="12" md="3">
                  <v-autocomplete
                    v-model="document.organization_id"
                    :items="organizations"
                    item-text="label"
                    item-value="id"
                    :label="$t('STOCK.WAREHOUSES.ORGANIZATION') + '*'"
                    :disabled="!canSave || document.is_finalized_editable === true"
                    :rules="canSave ? [validations.required()] : []"
                    :error-messages="validationErrors.organization_id || []"
                    @change="clearHeaderValidationError('organization_id')"
                  ></v-autocomplete>
                </v-col>
                <v-col cols="12" md="3">
                  <v-autocomplete
                    v-model="document.source_warehouse_id"
                    :items="warehouseOptions"
                    item-text="label"
                    item-value="id"
                    :label="$t('STOCK.SALES.SOURCE_WAREHOUSE') + '*'"
                    :hint="$t('STOCK.SALES.SOURCE_WAREHOUSE_HINT')"
                    persistent-hint
                    :disabled="!canSave || document.is_finalized_editable === true"
                    :rules="canSave ? [validations.required()] : []"
                    :error-messages="validationErrors.source_warehouse_id || []"
                    @change="clearHeaderValidationError('source_warehouse_id')"
                  ></v-autocomplete>
                </v-col>
                <v-col cols="12" md="3">
                  <v-autocomplete
                    v-model="document.destination_company_id"
                    :items="companyOptions"
                    :search-input.sync="companySearch"
                    item-text="label"
                    item-value="id"
                    :label="$t('STOCK.SALES.CUSTOMER') + '*'"
                    :disabled="!canSave"
                    :rules="canSave ? [validations.required()] : []"
                    :error-messages="validationErrors.destination_company_id || []"
                    @change="clearHeaderValidationError('destination_company_id')"
                  ></v-autocomplete>
                </v-col>
                <v-col cols="12" md="3">
                  <v-autocomplete
                    v-model="document.deal_id"
                    :items="dealOptions"
                    item-text="label"
                    item-value="id"
                    :label="$t('STOCK.SALES_ORDERS.DEAL')"
                    :disabled="!canSave"
                    clearable
                    @change="onDealChange"
                  ></v-autocomplete>
                  <StockDealReference
                    :deal-id="document.deal_id"
                    :deal="document.deal || selectedDealOption"
                  />
                </v-col>
              </v-row>
              <v-row>
                <v-col cols="12" md="2">
                  <v-text-field
                    v-model="document.action_date"
                    type="date"
                    :label="$t('STOCK.SALES.DATE') + '*'"
                    :disabled="!canSave || document.is_finalized_editable === true"
                  ></v-text-field>
                </v-col>
                <v-col cols="12" md="2">
                  <v-text-field
                    v-model.number="document.default_vat_rate"
                    type="number"
                    suffix="%"
                    :label="$t('STOCK.SALES_ORDERS.DEFAULT_VAT')"
                    :disabled="!canSave"
                  ></v-text-field>
                </v-col>
                <v-col cols="12" md="2">
                  <v-text-field
                    v-model="document.currency_code"
                    :label="$t('STOCK.SALES_ORDERS.CURRENCY')"
                    :disabled="!canSave"
                  ></v-text-field>
                </v-col>
                <StockIncotermFields
                  :incoterm-id.sync="document.incoterm_id"
                  :incoterm-named-place.sync="document.incoterm_named_place"
                  :incoterm-options="incotermOptions"
                  :incoterm-disabled="incotermFieldsDisabled"
                />
              </v-row>

              <v-alert type="info" dense text class="mb-4">
                <template v-if="showSaleFinancials">
                  {{ $t("STOCK.COMMON.TOTALS_SALE_FINANCIAL", {
                    net: formatNum(document.total_net),
                    cogs: formatNum(document.total_cost_of_goods_net),
                    margin: formatNum(document.total_margin_net),
                    marginPct: formatNum(document.margin_pct),
                    vat: formatNum(document.total_vat),
                    gross: formatNum(document.total_gross),
                  }) }}
                </template>
                <template v-else>
                  {{ $t("STOCK.SALES_ORDERS.TOTALS_SUMMARY", {
                    net: formatNum(previewDocumentTotals.total_net),
                    vat: formatNum(previewDocumentTotals.total_vat),
                    gross: formatNum(previewDocumentTotals.total_gross),
                  }) }}
                </template>
              </v-alert>

              <v-subheader class="px-0">{{ $t("STOCK.SALES_ORDERS.LINES") }}</v-subheader>

              <v-alert v-if="linesLocked" type="info" dense text class="mb-3">
                {{ $t("STOCK.SALES.LINES_LOCKED_HINT") }}
              </v-alert>

              <v-alert v-if="!lines.length" type="info" dense text class="mb-3">
                {{ $t("STOCK.PRODUCT_FINDER.EMPTY_LINES") }}
              </v-alert>
              <div v-else class="stock-document-lines-wrap">
                <v-simple-table dense class="stock-document-lines">
                  <thead>
                    <tr>
                      <th class="stock-document-lines__sticky stock-document-lines__sticky--index">
                        #
                      </th>
                      <th
                        class="stock-document-lines__sticky stock-document-lines__sticky--product"
                      >
                        {{ $t("STOCK.PRODUCTS.PRODUCT") }}
                      </th>
                      <th class="stock-document-lines__col-qty">
                        {{ $t("STOCK.SALES_ORDERS.QTY") }}
                      </th>
                      <th class="stock-document-lines__col-qty">
                        {{ $t("STOCK.COMMON.GROSS_WEIGHT_KG") }}
                      </th>
                      <th class="stock-document-lines__col-money">
                        {{ moneyColumnHeader("STOCK.SALES_ORDERS.UNIT_PRICE_NET") }}
                      </th>
                      <th class="stock-document-lines__col-money">
                        {{ moneyColumnHeader("STOCK.SALES_ORDERS.LINE_TOTAL_NET") }}
                      </th>
                      <th v-if="showSaleFinancials" class="stock-document-lines__col-money">
                        {{ moneyColumnHeader("STOCK.COMMON.UNIT_COST_AVG") }}
                      </th>
                      <th v-if="showSaleFinancials" class="stock-document-lines__col-money">
                        {{ moneyColumnHeader("STOCK.COMMON.COST_OF_GOODS_NET") }}
                      </th>
                      <th v-if="showSaleFinancials" class="stock-document-lines__col-money">
                        {{ moneyColumnHeader("STOCK.COMMON.MARGIN_NET") }}
                      </th>
                      <th v-if="showSaleFinancials" class="stock-document-lines__col-vat">
                        {{ $t("STOCK.COMMON.MARGIN_PCT") }}
                      </th>
                      <th class="stock-document-lines__col-vat">
                        {{ $t("STOCK.SALES_ORDERS.VAT") }}
                      </th>
                      <th class="stock-document-lines__col-lot">
                        {{ $t("STOCK.SALES.LOT") }}
                      </th>
                      <th></th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr
                      v-for="(line, index) in lines"
                      :key="`line-${index}-${line.stock_product_id}`"
                      :class="{
                        'stock-document-lines__row--lot-error':
                          lineExceedsLotAvailable(line, lotQuantityFieldKey),
                        'stock-document-lines__row--no-stock':
                          lineHasNoAvailableLots(line),
                      }"
                    >
                      <td
                        class="stock-document-lines__sticky stock-document-lines__sticky--index"
                      >
                        {{ index + 1 }}
                      </td>
                      <td
                        class="stock-document-lines__sticky stock-document-lines__sticky--product stock-document-lines__product"
                      >
                        <div class="font-weight-medium">{{ lineProductLabel(line) }}</div>
                        <div class="text-caption grey--text">{{ lineProductMeta(line) }}</div>
                      </td>
                      <td class="stock-document-lines__col-qty">
                        <StockLineSyncedQtyFields
                          :line="line"
                          :disabled="!canEditSaleLineStructure(line)"
                          :error="lineExceedsLotAvailable(line, lotQuantityFieldKey)"
                          :kg-hint="
                            line.stock_lot_id
                              ? lineLotQuantityHint(line, lotQuantityFieldKey, lineUnitCode(line))
                              : lineUnitCode(line) || 'kg'
                          "
                          @focus="focusSelectAllInput"
                          @quantity-changed="onSaleLineQuantityInput(line)"
                        />
                      </td>
                      <td class="stock-document-lines__col-qty">
                        <StockLineField
                          v-model.number="line.gross_weight_kg"
                          type="number"
                          hint="kg"
                          :disabled="!canEditSaleLineFields(line)"
                          @focus="focusSelectAllInput"
                        />
                      </td>
                      <td class="stock-document-lines__col-money">
                        <StockLineField
                          v-model.number="line.unit_price_net"
                          type="number"
                          :disabled="!canEditSaleLineFields(line)"
                          @focus="focusSelectAllInput"
                          @input="onLineQtyOrUnitInput(line, lineQuantityKey)"
                        />
                      </td>
                      <td class="stock-document-lines__col-money">
                        <StockLineField
                          v-model.number="line.line_total_net"
                          type="number"
                          :disabled="!canEditSaleLineFields(line)"
                          @focus="focusSelectAllInput"
                          @input="onLineTotalNetInput(line, lineQuantityKey)"
                        />
                      </td>
                      <td v-if="showSaleFinancials" class="stock-document-lines__col-money">
                        <span class="stock-document-lines__readonly-money">
                          {{ formatLineMoney(line.unit_cost_net) }}
                        </span>
                      </td>
                      <td v-if="showSaleFinancials" class="stock-document-lines__col-money">
                        <span class="stock-document-lines__readonly-money">
                          {{ formatLineMoney(line.cost_of_goods_net) }}
                        </span>
                      </td>
                      <td v-if="showSaleFinancials" class="stock-document-lines__col-money">
                        <span
                          class="stock-document-lines__readonly-money"
                          :class="marginClass(line.margin_net)"
                        >
                          {{ formatLineMoney(line.margin_net) }}
                        </span>
                      </td>
                      <td v-if="showSaleFinancials" class="stock-document-lines__col-vat">
                        <span :class="marginClass(line.margin_net)">
                          {{ formatLineMarginPct(line.margin_pct) }}
                        </span>
                      </td>
                      <td class="stock-document-lines__col-vat">
                        <StockLineField
                          v-model.number="line.vat_rate"
                          type="number"
                          hint="%"
                          narrow
                          :disabled="!canEditSaleLineFields(line)"
                          @focus="focusSelectAllInput"
                          @input="onLineVatRateInput"
                        />
                      </td>
                      <td class="stock-document-lines__col-lot">
                        <template v-if="usesLotPicker(line)">
                          <v-autocomplete
                            v-if="canSave || canFinalize"
                            v-model="line.stock_lot_id"
                            :items="lotOptionsForLine(line)"
                            item-text="label"
                            item-value="id"
                            dense
                            hide-details
                            clearable
                            :error="lineExceedsLotAvailable(line, lotQuantityFieldKey)"
                            :disabled="linesLocked || (!canSave && !canFinalize)"
                            :placeholder="$t('STOCK.SALES.LOT_PLACEHOLDER')"
                            @change="onLotSelected(line, $event)"
                          ></v-autocomplete>
                          <span v-else>{{ lineLotDisplay(line) }}</span>
                          <StockLineLotAvailability
                            :product="line.stock_product"
                            :available-kg="lineLotAvailableQuantity(line, lotQuantityFieldKey)"
                          />
                        </template>
                        <span
                          v-else-if="lineHasNoAvailableLots(line)"
                          class="caption warning--text"
                        >
                          {{ $t("STOCK.SALES.NO_AVAILABLE_STOCK") }}
                        </span>
                        <span v-else>{{ lineLotDisplay(line) }}</span>
                      </td>
                      <td class="stock-document-lines__actions text-nowrap">
                        <v-btn
                          v-if="canEditSaleLineStructure(line) && line.stock_product_id"
                          icon
                          small
                          :title="$t('STOCK.SALES.DUPLICATE_PRODUCT_LINE')"
                          @click="duplicateProductLine(index)"
                        >
                          <v-icon small>mdi-plus</v-icon>
                        </v-btn>
                        <v-btn v-if="canEditLineStructure" icon small @click="removeLine(index)">
                          <v-icon small>mdi-delete</v-icon>
                        </v-btn>
                      </td>
                    </tr>
                  </tbody>
                </v-simple-table>
              </div>

              <StockProductLineFinder
                :disabled="!canEditLineStructure"
                @add="addProductFromFinder"
              />

              <v-subheader v-if="statusTimeline.length" class="px-0 mt-4">
                {{ $t("STOCK.SALES.STATUS_TIMELINE") }}
              </v-subheader>
              <v-timeline v-if="statusTimeline.length" dense class="sale-status-timeline">
                <v-timeline-item
                  v-for="entry in statusTimeline"
                  :key="entry.id"
                  small
                  :color="timelineEntryColor(entry)"
                >
                  <div class="subtitle-2">
                    {{ timelineEntryLabel(entry) }}
                  </div>
                  <div class="caption grey--text">
                    {{ formatTimelineDate(entry.changed_at) }}
                    <span v-if="entry.changed_by_user && entry.changed_by_user.name">
                      · {{ entry.changed_by_user.name }}
                    </span>
                  </div>
                  <div v-if="entry.notes" class="caption">{{ entry.notes }}</div>
                </v-timeline-item>
              </v-timeline>

              <StockDocumentChainLinks
                v-if="!isNew"
                show-transport-orders
                show-sales-orders
                show-invoices
                :source-transport-order="document.source_transport_order"
                :sales-orders="linkedSalesOrders"
                :invoices="document.invoices || []"
              />

              <StockDocumentFilesPanel
                v-if="!isNew"
                document-kind="sale"
                :document-id="saleId"
                :files="documentFiles"
                :can-regenerate="canRegenerateDocument"
                @files-updated="documentFiles = $event"
              />
            </v-card-text>
          </v-card>
        </v-tab-item>
      </v-tabs-items>
    </v-card>

    <StockInvoiceLinkDialog
      v-model="showInvoiceDialog"
      :action-id="saleId"
      kind="delivery"
      :title="$t('STOCK.INVOICES.CREATE_FROM_DELIVERY')"
      :hint="$t('STOCK.INVOICES.CREATE_FROM_DELIVERY_HINT', { code: document.code })"
      :show-pass-through="true"
      :submitting="isProcessing"
      @confirm="handleInvoiceLinkConfirm"
      @error="handleInvoiceLinkError"
    />
  </div>
</template>

<script>
import { debounce } from "lodash";
import StockProductLineFinder from "@/views/partials/stock/StockProductLineFinder.vue";
import StockDocumentFilesPanel from "@/views/partials/stock/StockDocumentFilesPanel.vue";
import StockDocumentChainLinks from "@/views/partials/stock/StockDocumentChainLinks.vue";
import StockDocumentPageHeading from "@/views/partials/stock/StockDocumentPageHeading.vue";
import StockDealReference from "@/views/partials/stock/StockDealReference.vue";
import StockIncotermFields from "@/views/partials/stock/StockIncotermFields.vue";
import StockLineField from "@/views/partials/stock/StockLineField.vue";
import StockLineSyncedQtyFields from "@/views/partials/stock/StockLineSyncedQtyFields.vue";
import StockLineLotAvailability from "@/views/partials/stock/StockLineLotAvailability.vue";
import stockDocumentLinesMixin from "@/views/partials/stock/stockDocumentLines.mixin";
import stockDocumentOptionsMixin from "@/views/partials/stock/stockDocumentOptions.mixin";
import stockDocumentTotalsMixin from "@/views/partials/stock/stockDocumentTotals.mixin";
import stockDocumentContextMixin from "@/views/partials/stock/stockDocumentContext.mixin";
import stockIncotermMixin from "@/views/partials/stock/stockIncoterm.mixin";
import stockStatusTimelineMixin from "@/views/partials/stock/stockStatusTimeline.mixin";
import stockLotQuantityValidationMixin from "@/views/partials/stock/stockLotQuantityValidation.mixin";
import { GET_ORGANIZATIONS_OPTIONS } from "@/store/organizations.module";
import { GET_STOCK_WAREHOUSE_OPTIONS } from "@/store/stock/stockWarehouses.module";
import { GET_COMPANIES_OPTIONS } from "@/store/companies.module";
import { SHOW_SNACK } from "@/store/snack.module";
import {
  GET_STOCK_SALE,
  CREATE_STOCK_SALE,
  UPDATE_STOCK_SALE,
  FINALIZE_STOCK_SALE,
  DISCARD_STOCK_SALE,
  GET_STOCK_SALE_DRAFT_FROM_SO,
  GET_STOCK_SALE_DRAFT_FROM_TO,
  GET_STOCK_SALE_SUGGEST_LOTS,
  GET_STOCK_SALE_SUGGEST_DRAFT_LOTS,
} from "@/store/stock/stockSales.module";
import { GET_STOCK_DEAL_OPTIONS } from "@/store/stock/stockReceipts.module";
import StockInvoiceLinkDialog from "@/views/partials/stock/invoices/StockInvoiceLinkDialog.vue";
import { CREATE_STOCK_INVOICE_FROM_DELIVERIES } from "@/store/stock/stockInvoices.module";

const emptyLine = (vat) => ({
  stock_product_id: null,
  quantity: 1,
  secondary_quantity: null,
  packaging_quantities: {},
  unit_id: null,
  unit_code: null,
  unit_price_net: 0,
  line_total_net: 0,
  gross_weight_kg: null,
  gross_to_net_ratio: null,
  vat_rate: vat,
  lot_number: "",
  stock_lot_id: null,
  sales_order_item_id: null,
});

export default {
  name: "StockViewSale",
  components: {
    StockProductLineFinder,
    StockLineField,
    StockLineSyncedQtyFields,
    StockLineLotAvailability,
    StockDocumentFilesPanel,
    StockDocumentChainLinks,
    StockDocumentPageHeading,
    StockDealReference,
    StockIncotermFields,
    StockInvoiceLinkDialog,
  },
  mixins: [
    stockDocumentLinesMixin,
    stockDocumentOptionsMixin,
    stockDocumentTotalsMixin,
    stockDocumentContextMixin,
    stockIncotermMixin,
    stockStatusTimelineMixin,
    stockLotQuantityValidationMixin,
  ],
  data() {
    return {
      lotQuantityFieldKey: "quantity",
      statusTimelineI18nPrefix: "STOCK.STATUSES.SALE",
      lineQuantityKey: "quantity",
      tab: "main",
      isProcessing: false,
      document: this.emptyDocument(),
      lines: [],
      lotSuggestions: [],
      lotSuggestionsReady: false,
      organizations: [],
      companyOptions: [],
      warehouseOptions: [],
      dealOptions: [],
      companySearch: null,
      validationErrors: {},
      documentFiles: [],
      draftMeta: {},
      showInvoiceDialog: false,
    };
  },
  computed: {
    saleId() {
      const id = this.$route.params.id;
      return id ? Number(id) : null;
    },
    isNew() {
      return !this.$route.params.id;
    },
    linkedSalesOrders() {
      return this.document.sales_orders || [];
    },
    incotermFieldsDisabled() {
      if (!this.canSave) {
        return true;
      }
      if (this.document.incoterm_locked) {
        return true;
      }
      return this.linkedSalesOrders.length > 0
        || this.lines.some((line) => line.sales_order_item_id);
    },
    canSave() {
      if (this.isReadOnlyDocument) {
        return false;
      }
      return this.isNew
        ? this.hasPermissions("stock-sales-create")
        : this.hasPermissions("stock-sales-edit");
    },
    linesLocked() {
      return Boolean(this.document.lines_locked);
    },
    canEditLineStructure() {
      return this.canSave && !this.linesLocked;
    },
    canFinalize() {
      return (
        !this.isNew &&
        ["draft", "expected"].includes(this.document.status) &&
        this.hasPermissions("stock-sales-finalize")
      );
    },
    canCreateInvoice() {
      return (
        !this.isNew &&
        this.document.status === "finalized" &&
        !this.document.is_on_issued_invoice &&
        this.hasPermissions("stock-invoices-create")
      );
    },
    invoiceLinkButtonLabel() {
      if (this.document.draft_invoice && this.document.draft_invoice.code) {
        return this.$t("STOCK.INVOICES.OPEN_DRAFT_INVOICE_SHORT", {
          code: this.document.draft_invoice.code,
        });
      }
      return this.$t("STOCK.INVOICES.CREATE_FROM_DELIVERY");
    },
    isPhysicalLotPickerEnabled() {
      // Lot picking applies to any source warehouse that holds lots, including
      // virtual drop-ship warehouses populated from virtual receipts. Finalize
      // requires a valid stock lot with available quantity for every delivery
      // (drop-ship included), so the picker must always be available.
      return true;
    },
    lotSuggestionSignature() {
      if (!this.isPhysicalLotPickerEnabled) {
        return "";
      }
      const productIds = [
        ...new Set(
          (this.lines || [])
            .map((line) => line.stock_product_id)
            .filter(Boolean)
            .map(Number)
        ),
      ].sort((a, b) => a - b);
      return `${this.document.source_warehouse_id || ""}|${productIds.join(",")}`;
    },
    isReadOnlyDocument() {
      if (this.isNew) {
        return false;
      }
      const slug = this.document.action_type && this.document.action_type.slug;
      const editableSlugs = ["sale_warehouse", "sale_dropship"];
      if (this.document.is_finalized_editable === true) {
        return false;
      }
      return !editableSlugs.includes(slug) || !["draft", "expected"].includes(this.document.status);
    },
    canDiscard() {
      if (this.isNew) {
        return true;
      }
      return (
        ["draft", "expected"].includes(this.document.status) &&
        this.hasPermissions("stock-sales-cancel")
      );
    },
    canRegenerateDocument() {
      return this.document.status === "finalized";
    },
    statusColor() {
      const map = {
        draft: "grey",
        expected: "orange",
        finalized: "success",
        cancelled: "error",
      };
      return map[this.document.status] || "grey";
    },
    statusLabel() {
      const key = `STOCK.STATUSES.SALE.${(this.document.status || "").toUpperCase()}`;
      return this.$te(key) ? this.$t(key) : this.document.status;
    },
    statusTimelineColorMap() {
      return {
        draft: "grey",
        expected: "orange",
        finalized: "success",
        reversed: "purple",
        cancelled: "error",
      };
    },
    showSaleFinancials() {
      if (!["finalized", "reversed"].includes(this.document.status)) {
        return false;
      }
      return this.document.total_cost_of_goods_net != null
        || (this.lines || []).some((line) => line.unit_cost_net != null);
    },
  },
  watch: {
    "$route.params.id"(id, previousId) {
      if (!id || id === previousId) {
        return;
      }
      if (String(this.document.id) === String(id)) {
        return;
      }
      this.loadSale(id);
    },
    companySearch: debounce(function () {
      this.loadCompanies();
    }, 300),
    "document.organization_id"() {
      this.runStockHeaderReloadOnOrganizationChange();
    },
    lotSuggestionSignature() {
      this.lotSuggestionsReady = false;
      this.queueLotSuggestionsLoad();
    },
  },
  created() {
    this.queueLotSuggestionsLoad = debounce(() => {
      this.loadLotSuggestions();
    }, 300);
  },
  mounted() {
    this.bootstrap();
  },
  methods: {
    emptyDocument() {
      return {
        organization_id: null,
        source_warehouse_id: null,
        destination_company_id: null,
        deal_id: null,
        action_date: new Date().toISOString().slice(0, 10),
        default_vat_rate: 20,
        currency_code: "EUR",
        exchange_rate_to_base: 1,
        incoterm_id: null,
        incoterm_named_place: null,
        incoterm_locked: false,
        lines_locked: false,
        notes: null,
        status: "draft",
        code: null,
        total_net: 0,
        total_vat: 0,
        total_gross: 0,
        sales_orders: [],
      };
    },
    computeLineNetFromUnit(line, quantityKey) {
      const qty = Number(line[quantityKey] ?? line.quantity ?? 0);
      const unitPrice = Number(line.unit_price_net || 0);
      return this.roundMoney(qty * unitPrice);
    },
    mapLineFromApi(item, data, quantityKey) {
      const qty = Number(item[quantityKey] ?? item.quantity ?? 0);
      const unitPrice = Number(item.unit_price_net || 0);
      const lineTotal =
        item.line_total_net != null
          ? Number(item.line_total_net)
          : this.roundMoney(qty * unitPrice);

      const line = {
        id: item.id,
        stock_product_id: item.stock_product_id,
        stock_product: item.stock_product || null,
        [quantityKey]: qty,
        unit_price_net: unitPrice,
        line_total_net: lineTotal,
        vat_rate: Number(item.vat_rate ?? data.default_vat_rate ?? 20),
        secondary_quantity: null,
        packaging_quantities: {},
        ...this.mapLineUnitFields(item),
      };

      return this.initializeLinePackagingQuantities(line);
    },
    onLineTotalNetInput(line, quantityKey) {
      const qty = Number(line[quantityKey] ?? line.quantity ?? 0);
      const total = Number(line.line_total_net || 0);
      line.line_total_net = total;
      if (qty > 0) {
        line.unit_price_net = this.roundMoney(total / qty);
      }
    },
    validateOrganizationAndWarehouseRequired() {
      let valid = true;
      const errors = { ...(this.validationErrors || {}) };

      if (!this.document.organization_id) {
        errors.organization_id = [this.$t("GENERAL.VALIDATION.REQUIRED_FIELD")];
        valid = false;
      } else {
        delete errors.organization_id;
      }

      if (!this.document.source_warehouse_id) {
        errors.source_warehouse_id = [this.$t("GENERAL.VALIDATION.REQUIRED_FIELD")];
        valid = false;
      } else {
        delete errors.source_warehouse_id;
      }

      if (!this.document.destination_company_id) {
        errors.destination_company_id = [this.$t("GENERAL.VALIDATION.REQUIRED_FIELD")];
        valid = false;
      } else {
        delete errors.destination_company_id;
      }

      if (this.validationErrors !== undefined) {
        this.validationErrors = errors;
      }

      if (!valid) {
        this.$store.dispatch(SHOW_SNACK, {
          message: this.$t("STOCK.SALES.ORG_WAREHOUSE_AND_CUSTOMER_REQUIRED_SAVE"),
          color: "error",
        });
      }

      return valid;
    },
    organizationIdsForWarehouseQuery() {
      const docOrgId = this.document.organization_id
        ? Number(this.document.organization_id)
        : null;

      if (docOrgId) {
        return [docOrgId];
      }

      const contextOrgIds = (this.$store.getters.stockContextOrganizationIds || []).map(Number);
      if (contextOrgIds.length > 0) {
        return contextOrgIds;
      }

      return [];
    },
    async loadOrganizations() {
      const contextIds = (this.$store.getters.stockContextOrganizationIds || []).map(Number);
      const params = {};
      if (contextIds.length > 0) {
        params.organization_ids = contextIds;
      }

      const orgs = await this.$store.dispatch(GET_ORGANIZATIONS_OPTIONS, params);
      let list = orgs.data || orgs;

      if (contextIds.length > 0) {
        list = list.filter((org) => contextIds.includes(Number(org.id)));
      }

      this.organizations = list;
    },
    async loadWarehouses() {
      const params = {
        organization_ids: this.organizationIdsForWarehouseQuery(),
      };
      const contextWarehouseIds = (this.$store.getters.stockContextWarehouseIds || []).map(Number);
      const selectedWarehouseIds = [
        this.document.destination_warehouse_id,
        this.document.source_warehouse_id,
      ]
        .filter(Boolean)
        .map(Number);
      const warehouseIdsParam = [
        ...new Set([...contextWarehouseIds, ...selectedWarehouseIds]),
      ];
      if (warehouseIdsParam.length > 0) {
        params.warehouse_ids = warehouseIdsParam;
      }

      const data = await this.$store.dispatch(GET_STOCK_WAREHOUSE_OPTIONS, params);
      const warehouses = data.data || data;
      let options = this.mapWarehouseOptions(warehouses, ["physical", "virtual"]);

      if (contextWarehouseIds.length > 0) {
        options = options.filter(
          (warehouse) =>
            contextWarehouseIds.includes(Number(warehouse.id)) ||
            selectedWarehouseIds.includes(Number(warehouse.id)) ||
            warehouse.type === "virtual"
        );
      }

      this.warehouseOptions = options;

      selectedWarehouseIds.forEach((warehouseId) => {
        const selected = warehouses.find(
          (warehouse) => Number(warehouse.id) === Number(warehouseId)
        );
        if (selected && ["physical", "virtual"].includes(selected.type)) {
          this.warehouseOptions = this.mergeOptionIntoList(this.warehouseOptions, {
            ...selected,
            label: selected.label || this.warehouseOptionLabel(selected),
          });
        }
      });

      if (typeof this.reconcileHeaderOptionsFromSeed === "function") {
        this.reconcileHeaderOptionsFromSeed();
      }
    },
    resetDocumentSelectionsOutsideContext() {
      if (this.suppressStockHeaderReload) {
        return;
      }

      const orgId = this.document.organization_id;
      if (
        orgId &&
        !this.organizations.some((org) => Number(org.id) === Number(orgId))
      ) {
        if (!(this.isNew && this.headerOptionsSeed)) {
          this.document.organization_id = null;
          this.document.source_warehouse_id = null;
        }
        return;
      }

      const warehouseId = this.document.source_warehouse_id;
      if (
        warehouseId &&
        !this.warehouseOptions.some(
          (warehouse) => Number(warehouse.id) === Number(warehouseId)
        )
      ) {
        if (typeof this.reconcileHeaderOptionsFromSeed === "function") {
          this.reconcileHeaderOptionsFromSeed();
        }
        if (
          !this.warehouseOptions.some(
            (warehouse) => Number(warehouse.id) === Number(warehouseId)
          ) &&
          !(this.isNew && this.headerOptionsSeed)
        ) {
          this.document.source_warehouse_id = null;
        }
      }
    },
    applyStockContextDefaultsOnNewDocument() {
      if (!this.isNew) {
        return;
      }

      if (!this.document.organization_id && this.organizations.length === 1) {
        this.document.organization_id = this.organizations[0].id;
      }

      if (!this.document.source_warehouse_id && this.warehouseOptions.length === 1) {
        this.document.source_warehouse_id = this.warehouseOptions[0].id;
      }

      this.applyRouteQueryDefaultsOnNewDocument();
    },
    async bootstrap() {
      this.isProcessing = true;
      try {
        await this.loadOrganizations();
        await Promise.all([
          this.loadCompanies(),
          this.loadWarehouses(),
          this.loadDeals(),
          this.loadIncoterms(),
        ]);
        if (!this.isNew) {
          await this.loadSale(this.$route.params.id);
        } else {
          await this.withSuppressedStockHeaderReload(async () => {
            if (this.hasStockDraftSourceQuery()) {
              await this.loadDraftFromQuery();
            } else {
              this.applyStockContextDefaultsOnNewDocument();
            }
          });
        }
      } catch (errors) {
        this.$store.dispatch(SHOW_SNACK, {
          message: (errors && errors.message) || this.$t("GENERAL.UNEXPECTED_ERROR"),
          color: "error",
        });
      } finally {
        this.isProcessing = false;
      }
    },
    async loadSale(id) {
      this.isProcessing = true;
      try {
        const data = await this.$store.dispatch(GET_STOCK_SALE, id);
        await this.applyDocument(data);
        await this.loadLotSuggestions();
      } catch (errors) {
        this.$store.dispatch(SHOW_SNACK, {
          message: (errors && errors.message) || this.$t("GENERAL.UNEXPECTED_ERROR"),
          color: "error",
        });
      } finally {
        this.isProcessing = false;
      }
    },
    async loadLotSuggestions() {
      if (!this.isPhysicalLotPickerEnabled) {
        this.lotSuggestions = [];
        this.lotSuggestionsReady = true;
        return;
      }

      this.lotSuggestionsReady = false;

      try {
        if (!this.isNew && this.saleId) {
          const data = await this.$store.dispatch(GET_STOCK_SALE_SUGGEST_LOTS, {
            saleId: this.saleId,
          });
          this.lotSuggestions = Array.isArray(data) ? data : data.lots || data.items || [];
          return;
        }

        const warehouseId = this.document.source_warehouse_id;
        const productIds = [
          ...new Set(
            (this.lines || [])
              .map((line) => line.stock_product_id)
              .filter(Boolean)
              .map(Number)
          ),
        ];

        if (!warehouseId || !productIds.length) {
          this.lotSuggestions = [];
          return;
        }

        const data = await this.$store.dispatch(GET_STOCK_SALE_SUGGEST_DRAFT_LOTS, {
          params: {
            source_warehouse_id: warehouseId,
            stock_product_ids: productIds,
            as_of_date: this.document.action_date,
          },
        });
        this.lotSuggestions = Array.isArray(data) ? data : data.lots || data.items || [];
      } catch (e) {
        this.lotSuggestions = [];
      } finally {
        this.lotSuggestionsReady = true;
      }
    },
    lineHasNoAvailableLots(line) {
      if (!this.isPhysicalLotPickerEnabled) {
        return false;
      }
      if (!line?.stock_product_id || line.stock_lot_id) {
        return false;
      }
      if (!this.document.source_warehouse_id || !this.lotSuggestionsReady) {
        return false;
      }
      return this.lotOptionsForLine(line).length === 0;
    },
    canEditSaleLineFields(line) {
      return this.canSave && !this.lineHasNoAvailableLots(line);
    },
    canEditSaleLineStructure(line) {
      return this.canEditLineStructure && !this.lineHasNoAvailableLots(line);
    },
    lotOptionsForLine(line) {
      if (!line.stock_product_id) {
        return [];
      }

      return this.mapLotPickerOptions(line, this.lotSuggestions);
    },
    recalculateGrossWeightFromLot(line) {
      const option = this.selectedLotOptionForLine(line);
      const ratio = option?.default_gross_to_net_ratio;
      if (!ratio || Number(ratio) <= 0) {
        line.gross_weight_kg = null;
        return;
      }

      line.gross_weight_kg = Math.round(
        Number(line.quantity || 0) * Number(ratio) * 10000
      ) / 10000;
    },
    onSaleLineQuantityInput(line) {
      this.onLineQtyOrUnitInput(line, this.lineQuantityKey);
      this.recalculateGrossWeightFromLot(line);
    },
    onLotSelected(line, lotId) {
      if (!lotId) {
        line.lot_number = "";
        line.gross_weight_kg = null;
        return;
      }
      const option = this.lotOptionsForLine(line).find(
        (item) => Number(item.id) === Number(lotId)
      );
      if (option) {
        line.lot_number = option.lot_number || option.label || "";
        this.recalculateGrossWeightFromLot(line);
      }
    },
    async applyDocument(data) {
      await this.withSuppressedStockHeaderReload(async () => {
        this.document = {
          ...this.emptyDocument(),
          ...data,
          ...this.mapSaleDocument(data),
        };
        await this.reloadSaleHeaderOptions();
        if (this.isNew) {
          this.setHeaderOptionsSeed(data, "syncSaleHeaderOptions");
        } else {
          this.clearHeaderOptionsSeed();
        }
        this.syncSaleHeaderOptions(data);
      });
      this.lines = (data.items || []).map((item) => ({
        ...this.mapLineFromApi(item, data, this.lineQuantityKey),
        lot_number: item.lot_number || item.stock_lot?.lot_number || "",
        stock_lot_id: item.stock_lot_id || item.stock_lot?.id || null,
        stock_lot: item.stock_lot || null,
        sales_order_item_id: item.sales_order_item_id,
        stock_transport_order_item_id: item.stock_transport_order_item_id,
        stock_transport_order_stop_item_id: item.stock_transport_order_stop_item_id,
        gross_weight_kg: item.gross_weight_kg,
        gross_to_net_ratio: item.gross_to_net_ratio,
        weight_basis: item.weight_basis,
        unit_cost_net: item.unit_cost_net,
        effective_unit_cost: item.effective_unit_cost,
        cost_of_goods_net: item.cost_of_goods_net,
        margin_net: item.margin_net,
        margin_pct: item.margin_pct,
      }));
      this.documentFiles = Array.isArray(data.files) ? data.files : [];
      this.syncIncotermFromDocument(data);
    },
    payload() {
      const params = {
        ...this.document,
        items: this.lines
          .filter((l) => l.stock_product_id)
          .map((line, index) => ({
            line_no: index + 1,
            stock_product_id: line.stock_product_id,
            quantity: line.quantity,
            unit_id: line.unit_id,
            gross_weight_kg: line.gross_weight_kg,
            unit_price_net: line.unit_price_net,
            vat_rate: line.vat_rate ?? this.document.default_vat_rate,
            lot_number: line.lot_number,
            stock_lot_id: line.stock_lot_id,
            sales_order_item_id: line.sales_order_item_id,
            stock_transport_order_item_id: line.stock_transport_order_item_id,
            stock_transport_order_stop_item_id: line.stock_transport_order_stop_item_id,
          })),
      };

      if (this.draftMeta.source_transport_order_id) {
        params.source_transport_order_id = this.draftMeta.source_transport_order_id;
      }

      return params;
    },
    async save() {
      if (!this.validateOrganizationAndWarehouseRequired()) {
        return;
      }
      if (!this.validateLotQuantitiesOrNotify(this.lotQuantityFieldKey)) {
        return;
      }
      this.isProcessing = true;
      try {
        this.validationErrors = {};
        const params = this.payload();
        let data;
        if (this.isNew) {
          data = await this.$store.dispatch(CREATE_STOCK_SALE, params);
          this.$router.replace({
            name: "stock-sales-view",
            params: { id: data.id },
          });
        } else {
          data = await this.$store.dispatch(UPDATE_STOCK_SALE, {
            id: this.$route.params.id,
            params,
          });
        }
        await this.applyDocument(data);
        await this.loadLotSuggestions();
        this.$store.dispatch(SHOW_SNACK, {
          message: this.$t("GENERAL.SUCCESS_MESSAGE"),
          color: "success",
        });
      } catch (errors) {
        this.validationErrors = (errors && errors.errors) || {};
        this.$store.dispatch(SHOW_SNACK, {
          message: (errors && errors.message) || this.$t("GENERAL.UNEXPECTED_ERROR"),
          color: "error",
        });
      } finally {
        this.isProcessing = false;
      }
    },
    async finalizeSale() {
      await this.save();
      this.isProcessing = true;
      try {
        const data = await this.$store.dispatch(FINALIZE_STOCK_SALE, this.$route.params.id);
        await this.applyDocument(data);
        this.$store.dispatch(SHOW_SNACK, {
          message: this.$t("STOCK.SALES.FINALIZED_MSG"),
          color: "success",
        });
      } catch (errors) {
        this.$store.dispatch(SHOW_SNACK, {
          message: (errors && errors.message) || this.$t("GENERAL.UNEXPECTED_ERROR"),
          color: "error",
        });
      } finally {
        this.isProcessing = false;
      }
    },
    openInvoiceDialog() {
      if (this.document.draft_invoice && this.document.draft_invoice.id) {
        this.$router.push({
          name: "stock-invoices-view",
          params: { id: this.document.draft_invoice.id },
        });
        return;
      }
      this.showInvoiceDialog = true;
    },
    handleInvoiceLinkConfirm(options) {
      if (options.openOnly && options.stock_invoice_id) {
        this.showInvoiceDialog = false;
        this.$router.push({
          name: "stock-invoices-view",
          params: { id: options.stock_invoice_id },
        });
        return;
      }
      this.createInvoice(options);
    },
    handleInvoiceLinkError(errors) {
      this.$store.dispatch(SHOW_SNACK, {
        message: (errors && errors.message) || this.$t("GENERAL.UNEXPECTED_ERROR"),
        color: "error",
      });
    },
    async createInvoice(options = {}) {
      this.isProcessing = true;
      try {
        const data = await this.$store.dispatch(CREATE_STOCK_INVOICE_FROM_DELIVERIES, {
          stock_action_ids: [Number(this.$route.params.id)],
          organization_id: this.document.organization_id,
          pass_through_extra_costs: options.pass_through_extra_costs || false,
          stock_invoice_id: options.stock_invoice_id,
        });
        this.showInvoiceDialog = false;
        this.$router.push({ name: "stock-invoices-view", params: { id: data.id } });
      } catch (errors) {
        this.$store.dispatch(SHOW_SNACK, {
          message: (errors && errors.message) || this.$t("GENERAL.UNEXPECTED_ERROR"),
          color: "error",
        });
      } finally {
        this.isProcessing = false;
      }
    },
    async loadDraftFromQuery() {
      const query = this.$route.query || {};
      const salesOrderId = query.sales_order_id;
      const transportOrderId = query.transport_order_id;

      if (!salesOrderId && !transportOrderId) {
        return;
      }

      try {
        const draftParams = {};
        if (query.stop_id) {
          draftParams.stop_id = query.stop_id;
        }

        const data = salesOrderId
          ? await this.$store.dispatch(GET_STOCK_SALE_DRAFT_FROM_SO, {
              id: salesOrderId,
              params: {},
            })
          : await this.$store.dispatch(GET_STOCK_SALE_DRAFT_FROM_TO, {
              id: transportOrderId,
              params: draftParams,
            });

        this.draftMeta = data.meta || {};
        await this.applyDocument(data);
        await this.loadLotSuggestions();
      } catch (errors) {
        const message =
          (errors?.errors && Object.values(errors.errors).flat()[0]) ||
          errors?.message ||
          this.$t("GENERAL.UNEXPECTED_ERROR");
        this.$store.dispatch(SHOW_SNACK, {
          message,
          color: "error",
        });
      }
    },
    async discardSale() {
      if (this.isNew) {
        this.$router.back();
        return;
      }

      if (!confirm(this.$t("STOCK.DOCUMENTS.CONFIRM_DISCARD"))) {
        return;
      }

      this.isProcessing = true;
      try {
        await this.$store.dispatch(DISCARD_STOCK_SALE, this.$route.params.id);
        this.$router.push({ name: "stock-movements" });
      } catch (errors) {
        this.$store.dispatch(SHOW_SNACK, {
          message: (errors && errors.message) || this.$t("GENERAL.UNEXPECTED_ERROR"),
          color: "error",
        });
      } finally {
        this.isProcessing = false;
      }
    },
    addProductFromFinder(product) {
      this.addProductLine(product, emptyLine);
    },
    duplicateProductLine(index) {
      const line = this.lines[index];
      if (!line?.stock_product_id || !line.stock_product) {
        return;
      }
      const newLine = this.insertProductLineAfter(line.stock_product, emptyLine, index);
      if (line.sales_order_item_id) {
        newLine.sales_order_item_id = line.sales_order_item_id;
      }
      newLine.unit_price_net = line.unit_price_net;
      newLine.vat_rate = line.vat_rate;
      newLine.line_total_net = this.computeLineNetFromUnit(newLine, this.lineQuantityKey);
    },
    removeLine(index) {
      this.lines.splice(index, 1);
    },
    moneyColumnHeader(i18nKey) {
      const label = this.$t(i18nKey);
      const ccy = this.document.currency_code;
      return ccy ? `${label} · ${ccy}` : label;
    },
    formatLineMoney(value) {
      if (value == null || value === "") {
        return "-";
      }
      return this.formatNum(value);
    },
    formatLineMarginPct(value) {
      if (value == null || value === "") {
        return "-";
      }
      return `${this.formatNum(value)}%`;
    },
    marginClass(value) {
      if (value == null || Number(value) === 0) {
        return "";
      }
      return Number(value) > 0 ? "success--text" : "error--text";
    },
    async loadCompanies() {
      const data = await this.$store.dispatch(GET_COMPANIES_OPTIONS, {
        search: this.companySearch,
      });
      this.companyOptions = data.data || data;
      this.preserveDealPartyCompaniesInOptions();
    },
    async loadDeals() {
      const responseData = await this.$store.dispatch(GET_STOCK_DEAL_OPTIONS, {
        organization_id: this.document.organization_id,
        limit: 50,
      });
      this.applyDealOptionsResponse(responseData);
    },
    formatNum(v) {
      return Number(v || 0).toFixed(2);
    },
  },
};
</script>

<style scoped>
.stock-view-page {
  padding-bottom: 48px;
  margin-bottom: 24px;
}
.stock-sale-linked-so a {
  font-weight: 500;
  text-decoration: none;
}
.stock-sale-linked-so a:hover {
  text-decoration: underline;
}
.stock-document-lines-wrap {
  overflow-x: auto;
  max-width: 100%;
  -webkit-overflow-scrolling: touch;
}
.stock-document-lines {
  border-collapse: separate;
  border-spacing: 0;
}
.stock-document-lines th,
.stock-document-lines td {
  vertical-align: top !important;
  padding-top: 8px !important;
  padding-bottom: 8px !important;
}
.stock-document-lines thead th {
  background: #f5f5f5;
  white-space: nowrap;
}
.stock-document-lines__sticky--index {
  position: sticky;
  left: 0;
  z-index: 2;
  width: 44px;
  min-width: 44px;
  max-width: 44px;
  background: #fff;
  text-align: center;
}
.stock-document-lines__sticky--product {
  position: sticky;
  left: 44px;
  z-index: 2;
  min-width: 260px;
  width: 260px;
  max-width: 280px;
  background: #fff;
  box-shadow: 4px 0 8px -4px rgba(0, 0, 0, 0.14);
}
.stock-document-lines thead .stock-document-lines__sticky {
  z-index: 4;
  background: #f5f5f5;
}
.stock-document-lines__product .font-weight-medium {
  line-height: 1.25;
}
.stock-document-lines__col-qty {
  min-width: 128px;
  vertical-align: top;
}
.stock-document-lines__col-money {
  min-width: 148px;
}
.stock-document-lines__col-vat {
  min-width: 88px;
}
.stock-document-lines__col-lot {
  min-width: 160px;
  vertical-align: top;
}
.stock-document-lines__actions {
  white-space: nowrap;
}
.stock-document-lines :deep(.stock-line-field__input) {
  width: 100%;
}
.stock-document-lines :deep(.stock-line-field__input .v-input__slot) {
  min-height: 36px !important;
}
.stock-document-lines__row--lot-error {
  background-color: rgba(244, 67, 54, 0.08);
}
.stock-document-lines__row--no-stock {
  background-color: rgba(255, 152, 0, 0.08);
}
</style>
